import { useState, useEffect, useCallback } from "react";
import { useSafeNavigate } from "utils/useSafeNavigate";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import unprocessedFaxDetailsService from "./UnprocessedFaxDetailsService";

/**
 * Custom hook to manage unprocessed fax details state and operations
 */
export const useUnprocessedFaxDetails = (faxInfoId, faxType = "") => {
  const navigate = useSafeNavigate();
  // Normalize fax type for comparison
  const normalizedFaxType = faxType?.toLowerCase() || "";
  const isSwitchFaxType = normalizedFaxType === "switch";
  const isNonSwitchFaxType = normalizedFaxType === "nonswitch";
  const isUnknownFaxType = normalizedFaxType === "unknown";

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fax details
  const [unprocessedFaxDetails, setUnprocessedFaxDetails] = useState(null);
  const [faxPriority, setFaxPriority] = useState("");

  // Link request state
  const [linkRequestStatus, setLinkRequestStatus] = useState(null);
  const [linkRequestError, setLinkRequestError] = useState(null);
  const [lastLinkWasCompletelyLinked, setLastLinkWasCompletelyLinked] = useState(false);

  // Discard state
  const [discardRequestCompleted, setDiscardRequestCompleted] = useState(false);

  // Note state
  const [note, setNote] = useState("");
  const [noteObject, setNoteObject] = useState(null);
  const [saveNoteLoading, setSaveNoteLoading] = useState(false);
  const [updateNoteLoading, setUpdateNoteLoading] = useState(false);
  const [deleteNoteLoading, setDeleteNoteLoading] = useState(false);

  // Tags state
  const [allAvailableTags, setAllAvailableTags] = useState([]);
  const [allSelectedTags, setAllSelectedTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsChanged, setTagsChanged] = useState(false);
  const [saveTagsLoading, setSaveTagsLoading] = useState(false);
  const [linkAndArchiveLoading, setLinkAndArchiveLoading] = useState(false);
  const [linkedNpis, setLinkedNpis] = useState([]);
  const [linkedPages, setLinkedPages] = useState([]);
  const [fetchLinkedNpisLoading, setFetchLinkedNpisLoading] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);

      try {
        // For Unknown type, fetch fax program codes; otherwise fetch regular tags
        const tagsPromise = isUnknownFaxType
          ? unprocessedFaxDetailsService.fetchFaxProgramTags().catch(() => [])
          : unprocessedFaxDetailsService.fetchAllTags().catch(() => []);

        const [faxDetails, noteData, allTags, faxTags] = await Promise.all([
          unprocessedFaxDetailsService.fetchUnprocessedFaxDetails(faxInfoId),
          unprocessedFaxDetailsService.fetchNoteForUnlinkedFax(faxInfoId).catch(() => null),
          tagsPromise,
          unprocessedFaxDetailsService.fetchFaxTags(faxInfoId).catch(() => []),
        ]);

        setUnprocessedFaxDetails(faxDetails);
        setFaxPriority(faxDetails?.faxPriority || "");

        if (noteData) {
          setNoteObject(noteData);
          setNote(noteData.note || "");
        }

        setAllAvailableTags(allTags);
        setAllSelectedTags(faxTags);
        setSelectedTags(faxTags);
      } catch (err) {
        setError(err.message);
        showNotification({
          title: "Error",
          message: err.message || "Failed to fetch fax details",
          position: "tr",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (faxInfoId) {
      fetchInitialData();
    }
  }, [faxInfoId]);

  // Fetch linked NPIs/pages for NON_SWITCH and SWITCH types (fax-archive backed)
  useEffect(() => {
    const fetchLinkedNpis = async () => {
      
      if (isUnknownFaxType || !faxInfoId) return;

      setFetchLinkedNpisLoading(true);
      try {
        const linkedNpisData = await unprocessedFaxDetailsService.fetchLinkedNpisForFax(faxInfoId);
        setLinkedNpis(linkedNpisData || []);
        
        // Extract linked pages from the linked NPIs data
        // linkPages is a string (comma-separated page numbers)
        const pages = new Set();
        if (Array.isArray(linkedNpisData)) {
          linkedNpisData.forEach((dto) => {
            if (dto.linkPages) {
              // linkPages is a comma-separated string
              const pagesStr = String(dto.linkPages || '');
              pagesStr.split(',').forEach((page) => {
                const pageNum = parseInt(page.trim(), 10);
                if (!isNaN(pageNum) && pageNum > 0) {
                  pages.add(pageNum);
                }
              });
            }
          });
          setLinkedPages(Array.from(pages).sort((a, b) => a - b));
        }
      } catch (err) {
        // Silently fail - if API doesn't exist yet or returns error, just don't set linked pages
        console.warn("Failed to fetch linked NPIs:", err.message);
        setLinkedNpis([]);
        setLinkedPages([]);
      } finally {
        setFetchLinkedNpisLoading(false);
      }
    };

    fetchLinkedNpis();
  }, [faxInfoId, isUnknownFaxType]);

  // Handle link request status changes
  useEffect(() => {
    if (linkRequestStatus === "success") {
      showNotification({
        title: `Switch Linking Request Sent Successfully`,
        message: `Fax Linking is in progress`,
        position: "tr",
        type: "success",
      });

      setLinkRequestStatus(null);
      if (isSwitchFaxType) {
        if (lastLinkWasCompletelyLinked) {
          setTimeout(() => {
            navigate("/unprocessed-fax/fax-list");
          }, 2000);
        } else {
          return
        }
      }

      const samePagePath = "/fax-details";
      const isSamePage = window.location.pathname.includes(samePagePath);
      if (isSamePage) {
        navigate(-1);
      }      
    } else if (linkRequestStatus === "failure") {
      showNotification({
        title: `Error Linking Fax Page(s)!`,
        message: linkRequestError || getErrorMessage("SOMETHING_WENT_WRONG"),
        position: "tr",
        type: "error",
      });
      setLinkRequestStatus(null);
      setLinkRequestError(null);
    }
  }, [
    linkRequestStatus,
    discardRequestCompleted,
    linkRequestError,
    navigate,
    isSwitchFaxType,
    lastLinkWasCompletelyLinked,
  ]);

  // Handle discard completion
  useEffect(() => {
    if (discardRequestCompleted) {
      showNotification({
        title: `Fax Discarded!`,
        message: `Fax has been discarded successfully`,
        position: "tr",
        type: "success",
      });
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    }
  }, [discardRequestCompleted, navigate]);

  // Link fax to switch
  const linkUnprocessedFaxToSwitch = useCallback(
    async (faxId, linkPages, isFaxCompletelyLinked = false) => {
      setLinkRequestStatus("loading");
      setLinkRequestError(null);
      setLastLinkWasCompletelyLinked(Boolean(isFaxCompletelyLinked));

      try {
        await unprocessedFaxDetailsService.linkUnprocessedFaxToSwitch(
          faxId,
          linkPages,
          isFaxCompletelyLinked
        );
        setLinkRequestStatus("success");
      } catch (err) {
        setLinkRequestStatus("failure");
        setLinkRequestError(err.message);
        throw err;
      }
    },
    []
  );

  // Link and archive fax (for NON_SWITCH)
  const linkAndArchiveFax = useCallback(
    async (faxId, linkPages, isFaxCompletelyLinked = false) => {
      setLinkAndArchiveLoading(true);
      try {
        await unprocessedFaxDetailsService.linkAndArchiveFax(faxId, linkPages, isFaxCompletelyLinked);
        showNotification({
          title: "NPI Linked to Fax",
          message: "NPI has been linked to fax successfully.",
          position: "tr",
          type: "success",
        });

        if (isFaxCompletelyLinked) {
          setTimeout(() => {
            navigate("/unprocessed-fax/program-type-list");
          }, 2000);
        }
      } catch (err) {
        showNotification({
          title: "Error Linking NPI to Fax!",
          message: err.message || "Failed to link NPI to fax",
          position: "tr",
          type: "error",
        });
        throw err;
      } finally {
        setLinkAndArchiveLoading(false);
      }
    },
    [navigate]
  );

  // Discard fax
  const discardFax = useCallback(async () => {
    try {
      await unprocessedFaxDetailsService.discardFax(faxInfoId);
      setDiscardRequestCompleted(true);
    } catch (err) {
      showNotification({
        title: `Error Discarding Fax!`,
        message: err.message || "Failed to discard fax",
        position: "tr",
        type: "error",
      });
    }
  }, [faxInfoId]);

  // Complete linking page (for NON_SWITCH when pages are already linked)
  const completePageLinking = useCallback(async () => {
    try {
      await unprocessedFaxDetailsService.completePageLinking(faxInfoId);
      showNotification({
        title: "Linking Completed",
        message: "Fax linking has been completed successfully.",
        position: "tr",
        type: "success",
      });

      setTimeout(() => {
        navigate("/unprocessed-fax/program-type-list");
      }, 2000);
    } catch (err) {
      showNotification({
        title: "Error Completing Linking!",
        message: err.message || "Failed to complete linking",
        position: "tr",
        type: "error",
      });
    }
  }, [faxInfoId, navigate]);

  // Set priority
  const setPriorityOfUnprocessedFax = useCallback(
    async (priority) => {
      try {
        await unprocessedFaxDetailsService.setPriorityOfUnprocessedFax(faxInfoId, priority);
        setFaxPriority(priority);
        showNotification({
          title: "Priority updated",
          message: "Fax priority has been updated successfully.",
          position: "tr",
          type: "success",
        });
      } catch (err) {
        showNotification({
          title: "Error updating priority",
          message: err.message || "Failed to update fax priority.",
          position: "tr",
          type: "error",
        });
      }
    },
    [faxInfoId]
  );

  // Save note
  const saveNote = useCallback(async () => {
    setSaveNoteLoading(true);
    try {
      const noteObject = { note };
      const savedNote = await unprocessedFaxDetailsService.saveNote(faxInfoId, noteObject);
      setNoteObject(savedNote);
      setNote(savedNote.note || "");
      showNotification({
        title: `Note Saved!`,
        message: `Note has been saved successfully`,
        position: "tr",
        type: "success",
      });
    } catch (err) {
      showNotification({
        title: `Error Saving Note!`,
        message: err.message || "Failed to save note",
        position: "tr",
        type: "error",
      });
    } finally {
      setSaveNoteLoading(false);
    }
  }, [faxInfoId, note]);

  // Update note
  const updateNote = useCallback(async () => {
    if (!noteObject?.uflNoteUuid) return;

    setUpdateNoteLoading(true);
    try {
      const updatedNoteObject = { ...noteObject, note };
      const updatedNote = await unprocessedFaxDetailsService.updateNote(
        faxInfoId,
        updatedNoteObject
      );
      setNoteObject(updatedNote);
      setNote(updatedNote.note || "");
      showNotification({
        title: `Note Updated!`,
        message: `Note has been updated successfully`,
        position: "tr",
        type: "success",
      });
    } catch (err) {
      showNotification({
        title: `Error Updating Note!`,
        message: err.message || "Failed to update note",
        position: "tr",
        type: "error",
      });
    } finally {
      setUpdateNoteLoading(false);
    }
  }, [faxInfoId, note, noteObject]);

  // Delete note
  const deleteNote = useCallback(async () => {
    if (!noteObject?.uflNoteUuid) return;

    setDeleteNoteLoading(true);
    try {
      await unprocessedFaxDetailsService.deleteNote(noteObject.uflNoteUuid);
      setNote("");
      setNoteObject(null);
      showNotification({
        title: `Note Deleted!`,
        message: `Note has been deleted successfully`,
        position: "tr",
        type: "success",
      });
    } catch (err) {
      showNotification({
        title: `Error Deleting Note!`,
        message: err.message || "Failed to delete note",
        position: "tr",
        type: "error",
      });
    } finally {
      setDeleteNoteLoading(false);
    }
  }, [noteObject]);

  // Save tags
  const saveTags = useCallback(async () => {
    setSaveTagsLoading(true);
    try {
      await unprocessedFaxDetailsService.saveTags(faxInfoId, selectedTags);
      setAllSelectedTags(selectedTags);
      setTagsChanged(false);
      showNotification({
        title: "Tags Updated!",
        message: `Fax tags have been updated successfully.`,
        position: "tr",
        type: "success",
      });
    } catch (err) {
      showNotification({
        title: "Error Saving Tags!",
        message: err.message || "Failed to save tags",
        position: "tr",
        type: "error",
      });
    } finally {
      setSaveTagsLoading(false);
    }
  }, [faxInfoId, selectedTags]);

  // Handle tags change
  const handleTagsChange = useCallback(
    (selectedList) => {
      const isChanged =
        JSON.stringify(selectedList.sort()) !==
        JSON.stringify([...allSelectedTags].sort());
      setSelectedTags(selectedList);
      setTagsChanged(isChanged);
    },
    [allSelectedTags]
  );

  return {
    // State
    loading,
    error,
    unprocessedFaxDetails,
    faxPriority,
    linkRequestStatus,
    discardRequestCompleted,
    note,
    noteObject,
    allAvailableTags,
    allSelectedTags,
    selectedTags,
    tagsChanged,
    saveNoteLoading,
    updateNoteLoading,
    deleteNoteLoading,
    saveTagsLoading,
    linkAndArchiveLoading,
    linkedNpis,
    linkedPages,
    fetchLinkedNpisLoading,

    // Actions
    linkUnprocessedFaxToSwitch,
    linkAndArchiveFax,
    discardFax,
    completePageLinking,
    setPriorityOfUnprocessedFax,
    saveNote,
    updateNote,
    deleteNote,
    saveTags,
    handleTagsChange,
    setNote,
    setFaxPriority,
  };
};

